import type {
  CaseContext,
  IssueCoding,
  ProtocolStatus,
  RelevanceCriteria,
  ProtocolSectionMap,
} from '../../redux/slice/aiReviewSliceV2';
export const ProtocolIntelligenceStatusTypes = {
  // Step-1
  Uploaded: 'UPLOADED',
  AiInitiated: 'AIINITIATED',
  RegisteredFailed: 'REGISTERED_FAILED',
  Registered: 'REGISTERED',
  PendingFailed: 'PENDING_FAILED',
  // Step-2
  Pending: 'PENDING',
  CompletedFailed: 'COMPLETED_FAILED',
  // Step-3
  Completed: 'COMPLETED',
  DraftFailed: 'DRAFT_FAILED',
  FinalFail: 'FINAL_FAIL',
  FinalFailed: 'FINAL_FAILED',
  TimeoutFailed: 'TIMEOUT_FAILED',
  // Step-4
  Draft: 'DRAFT',
  // Step-5
  Final: 'FINAL',
} as const;

export type ProtocolIntelligenceStatus =
  (typeof ProtocolIntelligenceStatusTypes)[keyof typeof ProtocolIntelligenceStatusTypes];

/** Normalize BE aiStatus for case/separator-insensitive comparisons. */
export const normalizeProtocolAiStatus = (
  status?: string | null
): string =>
  String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

const PROTOCOL_UPLOAD_FAILED_STATUSES = [
  ProtocolIntelligenceStatusTypes.CompletedFailed,
  ProtocolIntelligenceStatusTypes.DraftFailed,
  ProtocolIntelligenceStatusTypes.FinalFail,
  ProtocolIntelligenceStatusTypes.FinalFailed,
  ProtocolIntelligenceStatusTypes.PendingFailed,
  ProtocolIntelligenceStatusTypes.RegisteredFailed,
  ProtocolIntelligenceStatusTypes.TimeoutFailed,
  'REGISTERED_FAIL',
].map(normalizeProtocolAiStatus);

export const isProtocolUploadCompletedStatus = (
  status?: string | null
): boolean =>
  normalizeProtocolAiStatus(status) ===
  normalizeProtocolAiStatus(ProtocolIntelligenceStatusTypes.Completed);

export const isProtocolUploadFailedStatus = (
  status?: string | null
): boolean =>
  PROTOCOL_UPLOAD_FAILED_STATUSES.includes(normalizeProtocolAiStatus(status));

export type IssueCode = {
  id?: string;
  code: string;
  question: string;
  description?: string;
};

export type JobType = 'ISSUECODE' | 'RELEVANCY';
export type JobStatus =
  | 'Draft'
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'SUBMITTED'
  | 'FAILED';

export interface IAIReviewJobGroup {
  jobGroupId: number;
  name: string;
  description: string;
  jobCount: number;
  createdAt: string;
  createdBy: string;
}

export interface IAIReviewJobGroupsResponse {
  jobGroups: IAIReviewJobGroup[];
  totalCount: number;
  totalJobGroupCount: number;
}

export interface ICreateAIReviewJobGroupPayload {
  name: string;
}

export interface ICreateAIReviewJobGroupResponse {
  jobGroupId: number;
  errorMessage: string;
}

export interface IUpdateAIReviewJobGroupPayload {
  jobGroupId: number;
  name: string;
}

export interface IUpdateAIReviewJobGroupResponse {
  jobGroupId: number;
  errorMessage: string;
}

export interface IDeleteAIReviewJobGroupPayload {
  jobGroupId: number;
}
export interface IDeleteAIReviewJobGroupResponse {
  jobGroupId: number;
  errorMessage: string;
}

export interface IDeleteAIReviewJobPayload {
  jobId: string | number;
}

export interface IDeleteAIReviewJobResponse {
  jobId: string | number;
}

export interface IRetryAIReviewJobPayload {
  jobId: string | number;
}

export interface IRetryAIReviewJobResponse {
  jobId: string | number;
  status: 'RETRY_QUEUE';
  message: string;
  totalDocs: number;
  retryCount: number;
}

export interface IAddJobToGroupPayload {
  groupId: string;
  jobId: string;
  addedBy?: string;
  reason?: string;
}

export interface IAddJobToGroupResponse {
  groupId: string;
  jobId: string;
  status: 'ADDED';
  addedAt: string;
  addedBy?: string;
}

export interface IRemoveJobFromGroupPayload {
  groupId: string;
  jobId: string;
}

export interface IRemoveJobFromGroupResponse {
  groupId: string;
  jobId: string;
  status: 'REMOVED';
  removedAt: string;
  removedBy?: string;
}

export interface IAIReviewProtocolIntelligencePayload {
  protocolId: number;
}

export interface ProtocolVersion {
  protocolId: number;
  createdBy: string | null; // "Lastname, Firstname"
  lastUpdatedAt?: string | null;
  isActive: 0 | 1; // 0 for false and 1 for true
  isApproved: boolean;
  isUploaded: boolean;
  protocolName: string;
  latestVersion: string;
  versionId?: number;
  status: string; // e.g. "Approved" | "Draft"
  source: string; // "manual" or uploaded filename
}

export interface ProtocolIntelligenceColumnFilter {
  columnName: string;
  value: string;
}

export interface ProtocolIntelligenceListRequest {
  sortingColumn: string;
  direction: 'asc' | 'desc';
  displayLength: number;
  pageNumber: number;
  columnFilter: ProtocolIntelligenceColumnFilter[];
}

export interface ProtocolVersionsResponse {
  totalRecords: number;
  protocolVersions: ProtocolVersion[];
}

export type UpdateProtocolActiveStatePayload = {
  protocolId: number;
  isActive: 0 | 1;
};

export type GenerateProtocolArtifactsPayload = {
  protocolId: number;
};

export type GenerateProtocolArtifactsResponse = {
  status: string | number;
};

export interface ICommonProtocolIntelligenceResponse {
  protocolId: string;
  caseContext: string;
  relevanceCriteria: string;
  issueCodes: IssueCode[];
  isActive: 0 | 1; // 0 for false and 1 for true
  lastModifiedAt: string;
}
export interface IUpdateProtocolIntelligenceDetailsPayload {
  // protocolId is for param but not the part of payload
  protocolId: number;
  caseContext: string;
  relevanceCriteria: string;
  issueCodes: IssueCode[];
  isActive: 0 | 1; // 0 for false and 1 for true
}

export interface IUpdateProtocolIntelligenceDetailsResponse
  extends ICommonProtocolIntelligenceResponse {
  lastModifiedBy: string;
}

export interface ProtocolDetailsResponse {
  protocolId: number;
  aIstatus: ProtocolIntelligenceStatus;
  caseContext: string;
  relevanceCriteria: string;
  issueCodes: IssueCode[];
  createdAt: string; // ISO date string
  createdBy: string; // "Lastname, Firstname"
  isUploaded: boolean;
  isActive: 0 | 1; // 0 for false and 1 for true
  protocolName: string;
  isApproved: boolean;
  isSaved: boolean;
  parentProtocolId?: number;
}

export interface GetProtocolVersionHistoryPayload {
  protocolId: number;
}

export interface ProtocolVersionHistoryItem {
  parentVersionName: string;
  versionName: string;
  versionId: number;
  isCaseContextModified: boolean;
  isRelevancyProtocolModified: boolean;
  isIssueCodeModified: boolean;
  modifiedBy: string;
  modifiedAt: string;
  status: 'draft' | 'approved';
}

export type ProtocolVersionHistoryResponse = ProtocolVersionHistoryItem[];

export interface IAIReviewProtocolIntelligenceResponse
  extends IUpdateProtocolIntelligenceDetailsPayload {
  protocolName: string;
  aIstatus: ProtocolIntelligenceStatus;
  createdBy: string | null; // "Lastname, Firstname"
  createdAt: string | null; // "Lastname, Firstname"
  isActive: 0 | 1; // 0 for false and 1 for true
  isApproved: boolean;
  isUploaded: boolean;
}

export type ProtocolIntelligenceFileUploadResponse = {
  protocolId: number;
};

export interface AIReviewCreateProtocolIntelligencePayload {
  file: File | null;
}

export interface IGetAIReviewSourceTypeResponse {
  id: number;
  sourceType: string;
}

export type CreateIssueCode = {
  code: string;
  question: string;
};

/** AVR-22264 create/update job status. Draft = save draft; Submitted = submit. */
export type AIReviewJobStatus = 'Draft' | 'Submitted';

export interface ICreateAIReviewJobBasePayload {
  jobType: JobType;
  jobName: string;
  description: string;
  groupID?: number | null;
  groundTruthTagId?: number | null;
  sourceType: string;
  sourceID: number;
  reviewProtocolVersionID?: number | null;
  parentProtocolVersionID?: number | null;
  isProtocolModified: boolean;
  parentReviewJobID?: number | null;
  isManual?: boolean;
  status: AIReviewJobStatus;
}

export interface ICreateIssueCodeJobPayload
  extends ICreateAIReviewJobBasePayload {
  jobType: 'ISSUECODE';
  /** Sparse JSON: `{}` when empty; only filled fields when partial. */
  caseContext?: Partial<CaseContext>;
  /** Sparse JSON: `{}` when empty; only filled fields when partial. */
  issueCoding?: Partial<IssueCoding>;
}

export interface ICreateRelevancyJobPayload
  extends ICreateAIReviewJobBasePayload {
  jobType: 'RELEVANCY';
  /** Sparse JSON: `{}` when empty; only filled fields when partial. */
  caseContext?: Partial<CaseContext>;
  /** Sparse JSON: `{}` when empty; only filled fields when partial. */
  relevancy?: Partial<RelevanceCriteria>;
}

export type ICreateAIReviewJobPayload =
  | ICreateIssueCodeJobPayload
  | ICreateRelevancyJobPayload;

/**
 * AVR-22269 edit job body. Same V2 contract as create, plus jobId in the path.
 * ISSUECODE sends `issueCoding` (not `issueCodes`).
 */
export type IUpdateAIReviewJobPayload = ICreateAIReviewJobPayload & {
  jobId: number;
};
export interface ICreateAIReviewJobResponse {
  jobId: number; // 0 if not created
  errorMessage: string; // empty if no error
}

export interface IUpdateAIReviewJobResponse {
  jobId: number; // 0 if not created
  errorMessage: string; // empty if no error
}

/** Submit uses the same create-job contract with status Submitted. */
export type ISubmitAIReviewJobPayload = ICreateAIReviewJobPayload;
export type ISubmitAIReviewJobResponse = ICreateAIReviewJobResponse;

export interface IGetAIReviewJobDetailsPayload {
  jobId: number | string;
}

export interface JobDetailsIssueCode {
  code: string;
  question: string;
}

export type AIReviewJobType = 'ISSUECODE' | 'RELEVANCY';

export interface IGetAIReviewJobDetailsResponse {
  jobId: number;
  jobName: string;
  jobType: AIReviewJobType;
  aiReviewJobGUID?: string;
  protocolName?: string;
  reviewProtocolVersion?: string;
  reviewProtocolVersionGUID?: string;
  parentProtocolVersion?: string;
  description: string;
  createdBy: string;
  createdOn?: string;
  createdAt?: string;
  firstName?: string;
  lastName?: string;
  status: JobStatus | string;
  sourceID: number;
  sourceType: string;
  sourceName: string;
  /** V2 JSON. GET may return `{}` when empty. */
  caseContext?: Partial<CaseContext>;
  /** V2 Issue Coding JSON. API field name is `issueCodes`. */
  issueCodes?: Partial<IssueCoding>;
  groundTruthTagID?: number | null;
  groundTruthTagId?: number | null;
  /** V2 relevancy JSON. GET may return `{}` when empty. */
  relevanceCriteria?: Partial<RelevanceCriteria>;
  groupID: number | null;
  groupName: string;
  reviewProtocolID?: number | null;
  parentProtocolID?: number | null;
  reviewProtocolVersionID?: number | null;
  parentProtocolVersionID?: number | null;
  isProtocolModified: boolean;
  parentReviewJobID?: number | null;
  isSaved: boolean;
  isManual: boolean;
  isCustom?: boolean;
  isDraftProtocolChangedAfterJobModified?: boolean;
  /** V2 structured artifacts when returned directly on the job details response. */
  structuredCaseContext?: CaseContext;
  structuredIssueCoding?: IssueCoding;
  structuredRelevanceCriteria?: RelevanceCriteria;
}
export interface IAIReviewEditJobNamePayload {
  jobId: string;
  jobName: string;
}

export interface IAIReviewEditJobNameResponse {
  jobId: string;
  name: string;
  updatedBy: string;
  updatedAt: string;
}

export interface IAIReviewJobsExportColumnFilter {
  columnName: string;
  columnValue: string;
}

export interface IAIReviewJobsExportRequest {
  sortingColumn?: string;
  direction?: 'ASC' | 'DESC';
  displayLength?: number;
  pageNumber?: number;
  columnFilter?: IAIReviewJobsExportColumnFilter[];
}

export type IAIReviewJobsExportResponse = string;

export interface IExportAIReviewJobPdfPayload {
  jobId: string | number;
}
export interface IGetAIReviewJobResultPayload {
  jobId: string | number;
  evaluationType?: 'issue-code' | 'relevancy';
}
export interface IGetAIReviewJobResultResponse {
  jobId: string | number;
  documentsInSelectedSource: number;
  excludedFromAI: number;
  submittedToAI: number;
  completed: number;
  errored?: number;
  Errored?: number;
  relevant?: number;
  nonRelevant?: number;
}

export interface IGetAIReviewDocErrorPanelPayload {
  page: number;
  pageSize: number;
  search?: string;
  sortBy: string;
  sortOrder: string;
  reviewJobID: string | number;
}

export interface IAIReviewDocErrorPanel {
  docID: string;
  errorMessage: string;
  documentID: string;
}

export interface IAIReviewDocErrorPanelResponse {
  items: IAIReviewDocErrorPanel[];
  total: number;
}

export interface IAIReviewRelevancyDocsPanel {
  docID: string;
  errorMessage: string;
  documentID: string;
}

export interface IAIReviewRelevancyDocsPanelResponse {
  items: IAIReviewRelevancyDocsPanel[];
  total: number;
}

export interface IDownloadAIReviewJobResultsPayload {
  jobId: number | string;
  jobType: AIReviewJobType;
}
export interface IAIReviewJobResultColumnFilter {
  columnName: string;
  value: string;
}

/** POST …/{jobId}/issuecoding/doc-result/view-by-issuecodes request body. */
export interface IAIReviewJobResultIssueCodePayload {
  sortingColumn: string;
  direction: string;
  columnFilter: IAIReviewJobResultColumnFilter[];
}

/** Single row in the View-by-Issue-Codes grid. */
export interface IAIReviewJobResultIssueCodeItem {
  reviewJobIssueCodeID: number;
  reviewJobIssueCode: string;
  question: string;
  relevantCount: number;
  nonRelevantCount: number;
}

/** POST …/view-by-issuecodes response. */
export interface IAIReviewJobResultIssueCodeResponse {
  issueCodeResults: IAIReviewJobResultIssueCodeItem[];
}

/** @deprecated Use IAIReviewJobResultIssueCodePayload */
export type IAIReviewJobResultIssueCodeListPayload =
  IAIReviewJobResultIssueCodePayload;

/** @deprecated Use IAIReviewJobResultIssueCodeItem */
export type IAIReviewJobResultIssueCodeListResponse =
  IAIReviewJobResultIssueCodeItem;

/** AVR-21305: View-by-Docs list request (POST …/issuecoding/doc-result/view-by-docs). */
export interface IAIReviewJobResultDocsListColumnFilter {
  columnName: string;
  value: string;
}
export interface IAIReviewJobResultRelevancyColumnFilter {
  columnName: string;
  value: string;
}
export interface IAIReviewJobResultDocsListPayload {
  sortingColumn: string;
  direction: string;
  displayLength: number;
  pageNumber: number;
  columnFilter: IAIReviewJobResultDocsListColumnFilter[];
}

/** AVR-21305: View-by-Docs grid row. */
export interface IAIReviewJobResultDocsListItem {
  documentId: number;
  reviewJobSubmittedId: number;
  docId: number;
  issueCodeMatchedCount: number;
  flag: string;
}

export interface IAIReviewJobResultDocsListResponse {
  totalDocCount: number;
  /** API spelling per contract (ReviewJobIssueCodes count). */
  totalIssuCodeCount: number;
  /** Corrected spelling some payloads use for the same total. */
  totalIssueCodeCount?: number;
  result: IAIReviewJobResultDocsListItem[];
}

export interface IAIReviewJobResultRelevancyListPayload {
  sortingColumn: string;
  direction: string;
  displayLength: number;
  pageNumber: number;
  columnFilter: IAIReviewJobResultRelevancyColumnFilter[];
}
export interface IAIReviewRelevancyResults {
  documentId: number;
  reviewJobSubmittedId: number;
  /** Display Doc ID from the API (e.g. "ID00000001"). */
  docId: string;
  relevancy: string;
  groundTruth: string;
  confidence: string;
  reason: string;
  isFlaggedForHumanReview: boolean;
}
export interface IAIReviewJobResultRelevancyListResponse {
  documents: IAIReviewRelevancyResults[];
  totalDocs: number;
}

/** AVR-22455 raw Relevancy row before API normalization. */
export interface IAIReviewRelevancyDocResultApiRow {
  documentId: number;
  reviewJobSubmittedId: number;
  docId: string | number;
  relevency: string;
  groundTruth: string;
  confidence: string;
  reason: string;
  isFlaggedForHumanReview: boolean;
}

export type RelevancyResponsiveness = 'Responsive' | 'Non-Responsive' | '';

/** AVR-22456: identity used to retrieve one Relevancy document summary. */
export interface IAIReviewRelevancyDocSummaryRequest {
  /** API path parameter casing is intentionally lower-case. */
  reviewjobid: number | string;
  reviewJobSubmittedId: number | string;
}

/** The subset of a Relevancy list row retained for opening document details. */
export interface IAIReviewRelevancyDocSelection {
  reviewJobSubmittedId: number;
  docId: string;
}

/** AVR-22456 response fields used by the Relevancy document-details panel. */
export interface IAIReviewRelevancyDocSummaryResponse {
  responsiveness: RelevancyResponsiveness;
  /** Backend responses may serialize this numeric value as a number or percentage string. */
  confidence: number | string;
  reason: string;
  tags: string[];
  isExcluded: boolean;
  excludedDetails: string;
  isFlaggedForHumanReview: boolean;
  humanReviewDetails: string;
  humanReviewTags: string[];
  /**
   * AVR-21306: present for Issue Coding document details.
   * Same doc-summary endpoint; Relevancy responses omit this field.
   */
  issueCodingResult?: IAIReviewIssueCodingResultItem[];
}

/** AVR-21306: per-issue-code row on Issue Coding document summary. */
export interface IAIReviewIssueCodingResultItem {
  issueCode: string;
  responsive: 'Yes' | 'No';
  confidence: number | string;
  reason: string;
}

/** Normalized AVR-22456 Relevancy document-summary model. */
export type IAIReviewRelevancyDocSummary = IAIReviewRelevancyDocSummaryResponse;

interface IColumnFilter {
  columnName: string;
  value: string | number;
}
export interface IGetJobListPayload {
  sortingColumn: string;
  direction: string;
  displayLength: number; // keep -1 for all the rows
  pageNumber: number;
  columnFilter: IColumnFilter[];
}
export enum artifactsStatus {
  custom = 'custom',
  modify = 'modify',
  saved = 'saved',
  manual = 'manual',
}
export interface IJobs {
  jobId: number;
  jobName: string;
  jobGroupId: number | null;
  jobGroupName: string | null;
  jobType: string;
  createdDate: string;
  executionDate: string | null;
  createdBy: string;
  sourceName: string | null;
  totalDocCount: number | null;
  completedDocCount: number | null;
  excludedErrorDocCount: number | null;
  status: string | null;
  protocolVersion: string;
  isCustom: boolean;
  isManual: boolean;
  isProtocolModified: boolean;
  isSaved: boolean;
}
export interface IGetJobListResponse {
  jobs: IJobs[];
  totalCount: number;
}

export interface IConfusionMatrix {
  truePositives: number;
  falsePositives: number;
  trueNegatives: number;
  falseNegatives: number;
}

export interface IMetrics {
  accuracy: number;
  precision: number;
  recall: number;
  f1Score: number;
}

export interface IGetGroundTruthEvaluationResponse {
  jobId: string | number;
  groundTruthName: string;
  groundTruthDocCount: number;
  confusionMatrix: IConfusionMatrix;
  metrics: IMetrics;
}

export interface IGroundTruthTag {
  tagId: number;
  tagLabel: string;
  documentCount?: number;
}

export interface IGetGroundTruthTagsResponse {
  tags: IGroundTruthTag[];
}

export interface IDeleteAIProtocolIntelligencePayload {
  protocolId: number;
}

export interface IDeleteAIProtocolIntelligenceResponse {
  protocolId: number;
}
export interface IValidateProtocolNameRequestPayload {
  protocolName: string;
}

/** AVR-21647: validate-name returns isDuplicate "Yes" | "No". */
export type ProtocolNameDuplicateFlag = 'Yes' | 'No' | string;

export interface IValidateProtocolNameResponse {
  protocolName: string;
  isDuplicate: ProtocolNameDuplicateFlag;
}

export interface IUpdateProtocolNamePayload {
  protocolId: number;
  protocolName: string;
}

/** PUT /protocols/{protocolId} returns 200 with a string body (may be empty). */
export type IUpdateProtocolNameResponse = string;
export interface ICreateNewProtocolPayloadProps {
  parentProtocolID: number | string;
  filename: string;
  caseContext: string;
  relevance: string;
  issueCodes?: IssueCode[];
}

export interface ICreateNewProtocolResponse {
  reviewProtocolId: number;
}

/** POST /protocols/save-as-new (AVR-22438). */
export interface ISaveProtocolAsNewPayload {
  protocolId: number;
  protocolName: string;
}

/** POST /protocols/save-as-new returns HTTP 200 OK with no body. */
export type ISaveProtocolAsNewResponse = void;

export interface IAIReviewNavigateColumnFilter {
  columnName: string;
  filterValue: string;
}

export interface IGetMasterDocIdForRelevancyPayloadBody {
  isInverse: boolean;
  selectedDocIDs: string;
  excludedDocIDs: string;
  documentCount: number;
  columnFilters?: IAIReviewNavigateColumnFilter[];
  search?: string;
}
export interface IGetMasterDocIdForRelevancyPayload {
  jobId: string | number;
  payloadBody: IGetMasterDocIdForRelevancyPayloadBody;
}
export interface IGetMasterDocIdForRelevancyAndDocResponse {
  doclistMasterId: number;
}

export interface IGetMasterDocIdForIssueCodingPayload {
  jobId: string | number;
  payloadBody: {
    reviewJobIssueCodeId: number;
    isRelevant: boolean;
  }[];
}

export interface IGetMasterDocIdForIssueCodingResponse {
  doclistMasterId: number;
}

export interface IAIReviewCloneHistoryItem {
  jobId: number;
  originalJobId: number | null;
  isProtocolModified: 'True' | 'False' | 'Original' | string;
  clonedAt?: string;
  clonedBy?: string;
  createdByName?: string;
  lastModifiedAt?: string;
  jobDetailsUrl?: string;
}

export interface IGetAIReviewCloneHistoryResponse {
  jobId: number;
  totalRecords: number;
  cloneHistory: IAIReviewCloneHistoryItem[];
}

export type docType =
  | 'all'
  | 'completed'
  | 'erroredexcluded'
  | 'excluded'
  | 'submitted'
  | 'relevant'
  | 'nonrelevant';

export interface IGetDocListMasterIdJobDocsPayload {
  reviewJobId: string | null;
  type: docType;
}

// Protocol Intelligence V2 API / navigation contracts
export interface ProtocolFileDetailsV2 {
  fileName: string;
  /** AVR-21735 / get version details */
  artifactId?: number | null;
  /** Legacy field still returned by some mocks */
  reviewProtocolDocumentID?: number | null;
  lastModifiedAt?: string | null;
  lastModifiedBy?: string | null;
}

/**
 * Create/edit body fileDetails when a protocol file was uploaded.
 */
export interface ICreateProtocolIntelligenceFileDetails {
  fileName: string;
  artifactId: number | null;
}

/**
 * AVR-21735: Create protocol — sections are partial (only filled keys; {} if empty).
 * fileDetails included only when a file was uploaded.
 */
export interface ICreateProtocolIntelligencePayload {
  protocolName: string;
  caseContext: Partial<CaseContext>;
  relevanceCriteria: Partial<RelevanceCriteria>;
  issueCoding: Partial<IssueCoding>;
  status: ProtocolStatus;
  fileDetails?: ICreateProtocolIntelligenceFileDetails;
}

export interface ICreateProtocolIntelligenceResponse {
  protocolId: number;
  versionId: number;
}

/**
 * AVR-21867: Create a new protocol version under an existing protocol.
 * Sections are partial (only filled keys; {} if empty).
 */
export interface ICreateProtocolVersionPayload {
  protocolId: number;
  parentVersionId: number;
  caseContext: Partial<CaseContext>;
  relevanceCriteria: Partial<RelevanceCriteria>;
  issueCoding: Partial<IssueCoding>;
  status: ProtocolStatus;
  fileDetails?: ICreateProtocolIntelligenceFileDetails;
}

export interface ICreateProtocolVersionResponse {
  protocolId: number;
  versionId: number;
}

export type GetProtocolVersionDetailsPayloadV2 = {
  versionId: number;
};

export interface ProtocolVersionDetailsResponseV2 {
  protocolName: string;
  caseContext: CaseContext;
  relevanceCriteria: RelevanceCriteria;
  issueCoding: IssueCoding;
  status: ProtocolStatus;
  fileDetails?: ProtocolFileDetailsV2;
}

export type UpdateProtocolVersionDetailsPayloadV2 = {
  versionId: number;
  protocolName?: string;
  caseContext?: Partial<CaseContext>;
  relevanceCriteria?: Partial<RelevanceCriteria>;
  issueCoding?: Partial<IssueCoding>;
  status?: ProtocolStatus;
  fileDetails?: ICreateProtocolIntelligenceFileDetails;
};

export interface UpdateProtocolVersionDetailsResponseV2 {
  protocolId: number;
  versionId: number;
}

export type ProtocolIntelligenceNavState = {
  protocolName?: string;
  protocolId?: number;
  versionId?: number;
  latestVersion?: string;
  status?: string;
  isApproved?: boolean;
};

export interface ProtocolNameItem {
  protocolId: number;
  protocolName: string;
}

export type GetProtocolNamesResponse = ProtocolNameItem[];

export type GetProtocolVersionsPayload = {protocolId: number};

export interface ProtocolVersionListItem {
  versionId: number;
  name: string;
  status: 'draft' | 'approved';
}

export interface GetProtocolVersionsResponse {
  versions: ProtocolVersionListItem[];
}
export interface IUploadProtocolPayload {
  file: File | null;
}

export type FileUploadResponse = {
  fileName: string;
  protocolArtifactId: number; // primary key of Review Protocol Document Id
};

export interface IUploadProtocolResponse extends ProtocolSectionMap {
  fileDetails: {
    fileName: string;
    reviewProtocolDocumentID: string | null;
  };
  status: string;
}

export interface ProtocolPdfDetails extends ProtocolVersionDetailsResponseV2 {
  latestVersion?: string;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
}
